'use client'

import type { ChangeEvent, FormEvent } from 'react'
import { useState, useEffect } from 'react'
import { Button, type ButtonProps } from '@/components/ui/button'
import { IconGoogle, IconApple } from '@/components/icons'
import { Logo } from '@/components/logo'
import { Tabs, TabsContent } from '@/components/ui/tabs'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import sendToBothAnalytics from '@/lib/mixpanel/sendToBothAnalytics'
import { useTranslations } from 'next-intl'
import { clearStoredReferralCode, getReferredByCode } from '@/lib/referral_code'
import { nanoid } from 'nanoid'
import { MIXPANEL_CLIENT_EVENT_NAMES } from '@/lib/mixpanel/constant'
import Turnstile from 'react-turnstile'
import {
  CONVERSION_EVENT_TYPES,
  sendConversionEventClient,
} from '@/lib/analytics/conversion'
import { getClientTrackingData } from '@/lib/analytics/conversion/utils'
import {
  SignInWithApple,
  type SignInWithAppleResponse,
  type SignInWithAppleOptions,
} from '@capacitor-community/apple-sign-in'
import { Capacitor } from '@capacitor/core'

interface FormData {
  email: string
  verificationCode: string
}

export function LoginSection({
  redirectUrl,
  mode,
}: {
  redirectUrl: string
  mode?: string
}) {
  const t = useTranslations('login')
  const isSignUp = mode === 'signup'
  const [isLoading, setIsLoading] = useState(false)
  const [showVerification, setShowVerification] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [hasResent, setHasResent] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)

  const [isMounted, setIsMounted] = useState(false)
  const [formData, setFormData] = useState<FormData>({
    email: '',
    verificationCode: '',
  })

  // Set mounted state to prevent hydration mismatch
  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  useEffect(() => {
    if (formData.verificationCode.length === 6) {
      handleVerificationSubmit()
    }
  }, [formData.verificationCode])

  const handleResendCode = async () => {
    if (countdown > 0) return
    // show turnstile widget and if it's verified, then resend the code
    setHasResent(true)
  }

  const handleResendTurnstileToken = async (token: string) => {
    setIsLoading(true)
    try {
      const response = await fetch('/api/auth/email-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-request-id': nanoid(12),
        },
        body: JSON.stringify({
          email: formData.email,
          turnstileToken: token,
        }),
      })

      if (!response.ok) {
        // if status is 429, show error message
        if (response.status === 429) {
          throw new Error('Too many requests, please try again later')
        }
        throw new Error('Failed to resend verification code')
      }

      toast.success('Verification code resent')

      setCountdown(60)
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to resend code',
      )
    } finally {
      setIsLoading(false)
      setHasResent(false)
    }
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [id.replace('signin-', '').replace('signup-', '')]: value
        .trim()
        .toLowerCase(),
    }))
  }

  const handleEmailSignIn = async (e: FormEvent) => {
    e.preventDefault()
    if (!turnstileToken) {
      toast.error('Please complete the security check')
      return
    }

    setIsLoading(true)

    sendToBothAnalytics(MIXPANEL_CLIENT_EVENT_NAMES.CLICK_SIGNIN, {
      email: formData.email,
      source: 'email',
    })

    const referralCode = getReferredByCode()

    try {
      const gaSignals = getClientTrackingData()

      const response = await fetch('/api/auth/email-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-request-id': nanoid(12),
        },
        body: JSON.stringify({
          email: formData.email,
          referralCode,
          turnstileToken,
          ga_client_id: gaSignals.ga_client_id,
          ga_session_id: gaSignals.ga_session_id,
          ga_session_number: gaSignals.ga_session_number,
          page_url: gaSignals.page_url,
          referrer_url: gaSignals.referrer_url,
          gclid: gaSignals.gclid,
          wbraid: gaSignals.wbraid,
          gbraid: gaSignals.gbraid,
          utm_source: gaSignals.utm_source,
          utm_medium: gaSignals.utm_medium,
          utm_campaign: gaSignals.utm_campaign,
          utm_term: gaSignals.utm_term,
          utm_content: gaSignals.utm_content,
        }),
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error('Too many requests, please try again later')
        }
        throw new Error(data.error || 'Failed to send verification code')
      }

      if (referralCode) {
        clearStoredReferralCode()
      }

      // Fire client-side signup event when a new user is created
      if (data?.is_new_user) {
        void sendConversionEventClient({
          event_type: CONVERSION_EVENT_TYPES.SIGNUP,
          user_id: data.user_id,
          user_email: formData.email,
          source: 'email',
          page_url: typeof window !== 'undefined' ? window.location.href : '',
          page_referrer:
            typeof document !== 'undefined' ? document.referrer : '',
        })
      }

      if (referralCode) {
        clearStoredReferralCode()
      }

      // Auto-login for test accounts
      if (data?.auto_login) {
        toast.success('Auto-login successful!')
        window.location.href = redirectUrl
        return
      }

      setShowVerification(true)
      setHasResent(false)
      setCountdown(0)
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to send verification code',
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerificationSubmit = async () => {
    setIsLoading(true)
    sendToBothAnalytics(MIXPANEL_CLIENT_EVENT_NAMES.CLICK_VERIFY_CODE, {
      email: formData.email,
    })
    try {
      const gaSignals = getClientTrackingData()

      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-request-id': nanoid(12),
        },
        body: JSON.stringify({
          email: formData.email,
          verificationCode: formData.verificationCode,
          ga_client_id: gaSignals.ga_client_id,
          ga_session_id: gaSignals.ga_session_id,
          ga_session_number: gaSignals.ga_session_number,
          page_url: gaSignals.page_url,
          referrer_url: gaSignals.referrer_url,
          gclid: gaSignals.gclid,
          wbraid: gaSignals.wbraid,
          gbraid: gaSignals.gbraid,
          utm_source: gaSignals.utm_source,
          utm_medium: gaSignals.utm_medium,
          utm_campaign: gaSignals.utm_campaign,
          utm_term: gaSignals.utm_term,
          utm_content: gaSignals.utm_content,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Verification failed')
      }

      toast.success('Signed in successfully')
      setShowVerification(false)
      // Fire client-side login event with user context
      if (data?.user?.id) {
        void sendConversionEventClient({
          event_type: CONVERSION_EVENT_TYPES.LOGIN,
          user_id: data.user.id,
          user_email: data.user.email,
          page_url: typeof window !== 'undefined' ? window.location.href : '',
          page_referrer:
            typeof document !== 'undefined' ? document.referrer : '',
        })
      }

      window.location.href = redirectUrl || '/'
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Verification failed',
        {
          position: 'top-center',
          duration: 5000,
        },
      )
    } finally {
      setIsLoading(false)
    }
  }

  const renderVerificationContent = () => (
    <div className="space-y-6">
      <div className="flex items-center mb-6">
        <Button
          variant="ghost"
          size="sm"
          className="p-0 hover:bg-transparent text-gray-900"
          onClick={() => setShowVerification(false)}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <title>Back to login form</title>
            <path
              d="M15 18L9 12L15 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Button>
        <h2 className="flex-1 text-center text-lg font-bold text-gray-900">
          {t('check_email_title')}
        </h2>
      </div>

      <div className="text-center text-gray-500 mb-8">
        {t('sent_code_to')}
        <br />
        <span className="font-medium text-gray-700">{formData.email}</span>
        <br />
        {t('confirm_with_code')}
      </div>

      <div className="flex justify-center gap-2">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Input
            key={i}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            className="w-12 h-12 text-center text-xl rounded-lg text-dark bg-white border-input focus-visible:border-input"
            value={formData.verificationCode[i] || ''}
            onChange={(e) => {
              const value = e.target.value
              if (value.length > 1) {
                const pastedValue = value.slice(0, 6)
                setFormData((prev) => ({
                  ...prev,
                  verificationCode: pastedValue,
                }))
                if (pastedValue.length === 6) {
                  const inputs = document.getElementsByName(`verification-${5}`)
                  if (inputs[0]) {
                    inputs[0].focus()
                  }
                }
              } else {
                setFormData((prev) => {
                  const newVerificationCode =
                    prev.verificationCode.substring(0, i) +
                    value +
                    prev.verificationCode.substring(i + 1)
                  return {
                    ...prev,
                    verificationCode: newVerificationCode,
                  }
                })
                if (value) {
                  const nextInput = document.getElementsByName(
                    `verification-${i + 1}`,
                  )
                  if (nextInput[0]) {
                    nextInput[0].focus()
                  }
                }
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && !formData.verificationCode[i]) {
                const prevInput = document.getElementsByName(
                  `verification-${i - 1}`,
                )
                if (prevInput[0]) {
                  prevInput[0].focus()
                }
              }
            }}
            onPaste={(e) => {
              e.preventDefault()
              const pastedData = e.clipboardData.getData('text').slice(0, 6)
              setFormData((prev) => ({
                ...prev,
                verificationCode: pastedData,
              }))
            }}
            name={`verification-${i}`}
          />
        ))}
      </div>

      <Button
        className="w-full bg-purple-600 hover:bg-purple-700 h-12 text-base font-medium"
        onClick={handleVerificationSubmit}
        disabled={
          isLoading ||
          !formData.verificationCode ||
          formData.verificationCode.length !== 6
        }
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t('verifying')}
          </>
        ) : (
          t('verify_code')
        )}
      </Button>

      <div className="flex flex-col items-center space-y-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleResendCode}
          disabled={countdown > 0 || isLoading}
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          {countdown > 0
            ? t('resend_code_in', { seconds: countdown })
            : t('resend_code')}
        </Button>
        {hasResent && (
          <Turnstile
            sitekey={
              process.env.NODE_ENV === 'development'
                ? '1x00000000000000000000AA'
                : process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || ''
            }
            onVerify={(token) => handleResendTurnstileToken(token)}
            theme="light"
            refreshExpired="auto"
            fixedSize={true}
          />
        )}
      </div>
    </div>
  )

  return (
    <>
      <div
        className="min-h-screen relative overflow-hidden"
        style={{
          background:
            'radial-gradient(ellipse at center, #00285B 0%, #000106 100%)',
        }}
      >
        {/* Logo */}
        <div className="absolute top-6 left-6 md:top-8 md:left-8 z-10">
          <Logo variant="light" />
        </div>

        {/* Content Container */}
        <div className="lg:grid lg:grid-cols-[1.2fr_0.8fr] gap-8 w-full mx-auto min-h-screen">
          {/* Left side with text */}
          <div className="flex items-center justify-center lg:justify-start lg:pl-20 pt-20 lg:pt-0 px-6 md:px-0">
            <div className="max-w-4xl text-white">
              <div className="relative">
                <h1 className="whitespace-pre-wrap text-[26px] xs:text-[28px] sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 leading-tight">
                  {t('hero.title')}
                  <span className="text-transparent bg-clip-text bg-gold-gradient relative inline-block filter drop-shadow-[0_2px_10px_rgba(255,215,0,0.3)] ml-2">
                    {t('hero.in_10_minutes')}
                    <svg
                      className="absolute w-full h-2 -bottom-1 left-0 text-gold opacity-60"
                      viewBox="0 0 100 10"
                      preserveAspectRatio="none"
                      role="presentation"
                    >
                      <path
                        d="M0 5 Q 50 10 100 5"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                      />
                    </svg>
                  </span>
                  {t('hero.with_ai')}
                </h1>
              </div>
              <p className="text-sm sm:text-base md:text-lg text-white mb-4 sm:mb-6 italic font-light">
                {t('hero.subtitle')}
              </p>
            </div>
          </div>

          {/* Right side with login form */}
          <div className="flex items-center justify-center p-6 lg:p-0 mt-10 md:mt-0">
            <div className="bg-white rounded-3xl p-6 md:p-8 w-full max-w-[400px] lg:max-w-[480px] shadow-xl">
              {!showVerification ? (
                <>
                  <h2 className="text-lg font-bold mb-6 md:mb-8 text-gray-900">
                    {isSignUp ? t('welcome_signup') : t('welcome')}
                  </h2>
                  <Tabs defaultValue="signin" className="w-full">
                    <GoogleLoginButton
                      redirectUrl={redirectUrl}
                      isSignUp={isSignUp}
                      className="mb-4"
                    />
                    <AppleLoginButton
                      redirectUrl={redirectUrl}
                      isSignUp={isSignUp}
                    />

                    <div className="relative my-6">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-200" />
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white text-gray-500">
                          {t('or_continue_with_mail')}
                        </span>
                      </div>
                    </div>

                    <TabsContent value="signin">
                      <form onSubmit={handleEmailSignIn} className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-dark" htmlFor="signin-email">
                            {t('email')}
                          </Label>
                          <Input
                            id="signin-email"
                            type="email"
                            autoComplete="email"
                            placeholder={t('email_placeholder')}
                            required
                            value={formData.email}
                            onChange={handleInputChange}
                            className="text-dark bg-white border-input focus-visible:border-input rounded-lg"
                          />
                        </div>

                        {/* Cloudflare Turnstile Widget using react-turnstile package */}
                        {isMounted && (
                          <div className="flex justify-center my-4">
                            <Turnstile
                              sitekey={
                                (process.env.NODE_ENV === 'development'
                                  ? '1x00000000000000000000AA'
                                  : process.env
                                      .NEXT_PUBLIC_TURNSTILE_SITE_KEY) || ''
                              }
                              onVerify={(token) => setTurnstileToken(token)}
                              theme="light"
                              refreshExpired="auto"
                              fixedSize={true}
                            />
                          </div>
                        )}

                        <Button
                          type="submit"
                          className="w-full"
                          disabled={isLoading || (!turnstileToken && isMounted)}
                        >
                          {isLoading
                            ? isSignUp
                              ? t('signing_up')
                              : t('signing_in')
                            : isSignUp
                              ? t('sign_up_with_email')
                              : t('sign_in_with_email')}
                        </Button>
                      </form>
                    </TabsContent>
                  </Tabs>
                </>
              ) : (
                renderVerificationContent()
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export function AppleLoginButton({
  redirectUrl,
  variant = 'outline',
  isSignUp = false,
}: {
  redirectUrl: string
  variant?: ButtonProps['variant']
  isSignUp?: boolean
}) {
  const t = useTranslations('login')
  const [isNative, setIsNative] = useState(false)

  useEffect(() => {
    setIsNative(Capacitor.isNativePlatform())
  }, [])

  if (!isNative) return null

  const handleLogin = async () => {
    try {
      const options: SignInWithAppleOptions = {
        clientId: 'ai.heyboss',
        redirectURI: 'https://heyboss.ai/api/login/apple/callback',
        scopes: 'name email',
        state: nanoid(),
        nonce: nanoid(),
      }

      const result: SignInWithAppleResponse =
        await SignInWithApple.authorize(options)

      if (result.response && result.response.identityToken) {
        const referralCode = getReferredByCode()

        const response = await fetch('/api/login/apple', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            identityToken: result.response.identityToken,
            user: result.response.user,
            referralCode,
          }),
        })

        const data = await response.json()

        if (response.ok && data.success) {
          if (referralCode) {
            clearStoredReferralCode()
          }
          toast.success('Signed in successfully')
          window.location.href = redirectUrl || '/'
        } else {
          throw new Error(data.error || 'Login failed')
        }
      } else {
        throw new Error('No identity token received')
      }
    } catch (error: any) {
      console.error('Apple Sign In Error:', error)
      if (
        error.message &&
        error.message.includes('AuthorizationError error 1000')
      ) {
        toast.error(
          'Sign In with Apple is currently unavailable. Please try again later or use another login method.',
        )
      } else {
        toast.error('Failed to sign in with Apple')
      }
    }
  }

  return (
    <Button
      variant={variant}
      size="lg"
      className="w-full mb-4"
      onClick={handleLogin}
    >
      <IconApple className="mr-2 h-5 w-5" />
      {isSignUp ? t('sign_up_with_apple') : t('sign_in_with_apple')}
    </Button>
  )
}

export function GoogleLoginButton({
  redirectUrl,
  variant = 'outline',
  isSignUp = false,
  className,
}: {
  redirectUrl: string
  variant?: ButtonProps['variant']
  isSignUp?: boolean
  className?: string
}) {
  const t = useTranslations('login')
  const handleLogin = () => {
    const referralCode = getReferredByCode()
    sendToBothAnalytics(MIXPANEL_CLIENT_EVENT_NAMES.CLICK_SIGNIN, {
      source: 'google',
    })
    const nextUrl = `/api/login/google?redirectUrl=${encodeURIComponent(
      redirectUrl,
    )}&referralCode=${encodeURIComponent(referralCode || '')}`

    if (referralCode) {
      clearStoredReferralCode()
    }

    window.location.href = nextUrl
  }

  return (
    <Button
      variant={variant}
      size="lg"
      className={`w-full ${className || ''}`}
      onClick={handleLogin}
    >
      <IconGoogle />
      {isSignUp ? t('sign_up_with_google') : t('sign_in_with_google')}
    </Button>
  )
}

export function GoogleLoginButton2({ redirectUrl }: { redirectUrl: string }) {
  const t = useTranslations('login')
  const handleLogin = () => {
    const referralCode = getReferredByCode()
    sendToBothAnalytics(MIXPANEL_CLIENT_EVENT_NAMES.CLICK_SIGNIN, {
      source: 'google',
    })
    const nextUrl = `/api/login/google?redirectUrl=${encodeURIComponent(
      redirectUrl,
    )}&referralCode=${encodeURIComponent(referralCode || '')}`

    if (referralCode) {
      clearStoredReferralCode()
    }

    window.location.href = nextUrl
  }

  return (
    <button
      type="button"
      className="w-full flex items-center justify-center gap-2 rounded-lg bg-white hover:bg-gray-50 text-gray-900 border border-gray-200 shadow-sm px-4 py-3 font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-gray-200"
      onClick={handleLogin}
    >
      <IconGoogle />
      {t('sign_in_with_google')}
    </button>
  )
}
